/**
 * Phase machine (DESIGN §5.1). ADVANCE_PHASE is the only way phases move; the
 * server's clock emits it at each deadline and faculty can emit it to skip.
 */
import type { Patient, Phase, Role, SessionState, TableState } from '../types.ts';
import { ROLE_ORDER } from '../types.ts';
import { DAY_TEAM, HANDOVER_WEIGHT, type DayConsultant } from '../content/dayteam.ts';
import { hashSeed } from '../prng.ts';
import { clockLength, emit, eventActive, listOf, routeOf, seatedRoles, type Ctx } from './common.ts';
import { enterArrivals } from './events.ts';
import { evaluateObjectives } from './objectives.ts';
import { expireLoans, logPhase, tickClose, tickOpen, tickOpenComplete } from './tick.ts';
import { commitDrafts, recordBoundary, startDraftTurn } from './drafts.ts';
import { resolveDiagnostics } from './act.ts';
import { commitTransfers, releaseTransfers } from './transfers.ts';
import { snapshotOverflow } from './overflow.ts';
import { releaseStaffLoans } from './team.ts';
export { setPhase };

/** Actions per seat per turn (DESIGN §5.3). Exported so the load study can try alternatives. */
export const BASE_ACTIONS: Record<Role, number> = { EM: 3, SURG: 2, PHYS: 2, PSYCH: 2, ICU: 2 };

/**
 * Four-seat overrides. Without a Psychiatrist, the two-route patients go down their medical
 * route, so the Physician takes the second extra action (D-41; docs/LOAD-STUDY.md §8).
 */
export const FOUR_SEAT_ACTIONS: Partial<Record<Role, number>> = { EM: 4, PHYS: 3 };

export function baseActions(t: TableState, role: Role): number {
  if (!t.fiveSeat && FOUR_SEAT_ACTIONS[role] !== undefined) return FOUR_SEAT_ACTIONS[role]!;
  return BASE_ACTIONS[role];
}

/** Phase duration in ms (scaled), or null for untimed phases. */
export function phaseDurationMs(s: SessionState, phase: Phase = s.clock.phase): number | null {
  const p = s.config.pacing;
  const scale = s.config.clockScale ?? 1;
  let ms: number | null;
  switch (phase) {
    case 'ARRIVALS':
      ms = p.arrivalsMs;
      if (s.tables.some((t) => t.prompts.some((q) => q.phase === 'ARRIVALS'))) ms += 20000;
      break;
    case 'HUDDLE': ms = p.huddleMs; break;
    case 'ACT': ms = p.actMs; break;
    case 'TICK_OPEN': ms = p.tickMs; break;
    case 'LOG': ms = null; break;
    case 'HANDOVER': ms = p.handoverMs; break;
    default: ms = null;
  }
  return ms === null ? null : Math.max(200, Math.round(ms * scale));
}

/** True when the current phase may end early (nothing waiting on a person). */
export function phaseComplete(s: SessionState): boolean {
  if (s.clock.phase === 'TICK_OPEN') return tickOpenComplete(s);
  return false;
}

function setPhase(ctx: Ctx, phase: Phase): void {
  ctx.s.clock.phase = phase;
  const d = phaseDurationMs(ctx.s, phase);
  ctx.s.clock.phaseEndsAt = d === null || ctx.s.clock.paused || ctx.s.clock.hold ? null : ctx.at + d;
  ctx.s.clock.remainingMs = ctx.s.clock.paused && d !== null ? d : null;
  emit(ctx, { type: 'PHASE', text: `Turn ${ctx.s.clock.turn}: ${phase}`, data: { phase, turn: ctx.s.clock.turn } });
}

function enterAct(ctx: Ctx): void {
  const s = ctx.s;
  const turn = s.clock.turn;
  for (const t of s.tables) {
    const theatre = t.bookings.find((b) => b.kind === 'THEATRE' && b.from <= turn && b.until !== null && b.until >= turn);
    t.board.secondTheatre = theatre ? { used: false, ventTaken: false } : null;
    for (const l of t.locums) if (l.arrivesTurn <= turn && (l.untilTurn === null || l.untilTurn >= turn)) l.actionsLeft = 2;
    for (const role of seatedRoles(t)) setActionBudget(ctx, t, role);
  }
}

/** Freeze one seat's release budget using explicit event modifiers. */
function setActionBudget(ctx: Ctx, t: TableState, role: Role): void {
  const s = ctx.s;
  const turn = s.clock.turn;
  const st = t.seats[role]!;
  const restricted = (role === 'PHYS' && eventActive(s, 'E8')) || (role === 'SURG' && eventActive(s, 'E11')) || (role === 'ICU' && eventActive(s, 'E15')) || ((role === 'ICU' || role === 'PHYS') && eventActive(s, 'E43'));
  const absent = role === 'PHYS' && t.effects.physicianAwayUntil >= turn;
  const base = absent ? 0 : restricted ? 1 : baseActions(t, role);
  let penalties = 0;
  if (t.effects.leadPenalty?.role === role && t.effects.leadPenalty.turn === turn) penalties++;
  if (t.effects.sickLeave?.role === role && turn <= t.effects.sickLeave.until) penalties++;
  for (const interrupt of t.interrupts.filter(i => i.seat === role && i.state === 'ESCALATED')) {
    interrupt.state = 'RESOLVED';
    penalties++;
    emit(ctx, { type: 'INTERRUPT_FORCED', table: t.no, seat: role, severity: 1, tag: 'Interruptions', text: `${interrupt.title} was left too long: it used up one action this turn.`, audience: ['faculty', `seat:${role}`] });
  }
  const bonus = (eventActive(s, 'E30') ? 1 : 0) + (eventActive(s, 'E31') && t.lead === role ? 1 : 0);
  let available = absent ? 0 : Math.max(0, base - penalties + bonus);
  if (eventActive(s, 'E25') && available > 0) available = Math.max(1, available - 1);
  st.actions = { available, spent: 0 };
}

function leaveAct(ctx: Ctx): void {
  for (const t of ctx.s.tables) {
    if (t.board.secondTheatre && !t.board.secondTheatre.used && t.timeOut.turn !== ctx.s.clock.turn) t.dashboard.idleCapacity++;
    for (const role of seatedRoles(t)) {
      const st = t.seats[role]!;
      if (st.actions.available > 0) st.stats.actionsLost += st.actions.available;
    }
  }
}

const round2 = (x: number) => Math.round(x * 100) / 100;

/** The day consultant a table hands over to: dealt from the session seed, independent of the game's draws. */
export function dayConsultant(s: { config: { seed: string } }, tableNo: number): DayConsultant {
  return DAY_TEAM[hashSeed(`${s.config.seed}:dayteam:${tableNo}`) % DAY_TEAM.length]!;
}

/**
 * 0700 handover score (D-42): the share of the patient's treatment route already done,
 * counting the Discharge step. No route, or a route for the wrong diagnosis, scores 0:
 * the day team inherits the whole problem. A patient who arrived on the handover turn
 * itself scores 1 (nobody could have started).
 */
export function handoverShare(s: SessionState, p: Patient): number {
  if (p.arrivedTurn === s.clock.turn) return 1;
  const r = routeOf(p);
  if (!r || !p.route || r.steps.length === 0) return 0;
  if (p.route.diag !== s.backs[p.id]?.truthKey) return 0;
  return Math.min(1, p.route.next / r.steps.length);
}

export function enterDebrief(ctx: Ctx, early = false): void {
  const s = ctx.s;
  settleShift(ctx, early);
  for (const t of s.tables) {
    const dc = dayConsultant(s, t.no);
    for (const role of ROLE_ORDER) {
      if (early) break;
      for (const p of listOf(s, t, role)) {
        const share = handoverShare(s, p);
        const harm = (1 - share) * HANDOVER_WEIGHT[p.colour] * dc.mult;
        t.dashboard.handedOver = round2(t.dashboard.handedOver + share);
        t.dashboard.harmed = round2(t.dashboard.harmed + harm);
        p.status = 'HANDED_OVER';
        t.handedOver.push(p.id);
        const pct = Math.round(share * 100);
        emit(ctx, { type: 'HANDED_OVER', table: t.no, seat: role, patient: p.id, severity: share < 0.5 ? 2 : 1, tag: 'Handover', text: `${p.name} handed over to ${dc.name}, ${pct}% through treatment: ${share.toFixed(2)} handed over, ${harm.toFixed(2)} harm.`, audience: ['faculty', `seat:${role}`], data: { share, harm } });
      }
    }
    for (const b of early ? [] : t.bounceSlot) {
      t.dashboard.harmed++;
      emit(ctx, { type: 'BOUNCE_AFTER_SHIFT', table: t.no, patient: b.id, severity: 2, tag: 'Premature closure; flow', text: `${s.patients[b.id]!.name} bounced back to the day team.` });
    }
    if (!early) t.bounceSlot = [];
  }
  evaluateObjectives(ctx);
}

/** Release the next cohort before opening the action window or handover. */
function releaseTurn(ctx: Ctx, start: number): void {
  const s = ctx.s;
  const before = patientBoundarySnapshot(s);
  const c = s.clock;
  if (c.phase === 'LOG') logPhase(ctx);
  c.turn++;
  releaseTransfers(ctx);
  releaseStaffLoans(ctx);
  setPhase(ctx, 'ARRIVALS');
  enterArrivals(ctx);
  const row = s.script[c.turn - 1];
  if (row?.handover) {
    setPhase(ctx, 'HANDOVER');
    for (const t of s.tables) {
      const dc = dayConsultant(s, t.no);
      emit(ctx, { type: 'DAY_TEAM', table: t.no, severity: 1, tag: 'Handover', text: `Table ${t.no} hands over to ${dc.name}. ${dc.line}`, audience: ['faculty', 'table'], data: { mult: dc.mult } });
    }
  } else {
    setPhase(ctx, 'ACT');
    enterAct(ctx);
    startDraftTurn(ctx);
    snapshotOverflow(ctx);
  }
  recordPatientChanges(ctx, before, start);
  recordBoundary(ctx, 'RELEASE', start, []);
}

type PatientBoundary = Pick<Patient, 'id' | 'name' | 'table' | 'colour' | 'ticks'> & {
  seat: Role;
  route: { code: string; completed: number } | null;
};

/** Retain only the visible fields of patients currently owned by each service. */
function patientBoundarySnapshot(s: SessionState): PatientBoundary[] {
  return Object.values(s.patients).flatMap(p => {
    if (p.status !== 'ACTIVE' || !p.seat) return [];
    const route = routeOf(p);
    return [{ id: p.id, name: p.name, table: p.table, seat: p.seat, colour: p.colour, ticks: p.ticks,
      route: route && p.route ? { code: p.route.code, completed: Math.min(p.route.next, route.steps.length) } : null }];
  });
}

/** Add observed own-patient changes absent from the existing boundary events. */
function recordPatientChanges(ctx: Ctx, before: PatientBoundary[], start: number): void {
  const after = new Map(patientBoundarySnapshot(ctx.s).map(p => [p.id, p]));
  const events = ctx.events.slice(start);
  const departures = ['DISCHARGED', 'EARLY_DISCHARGE', 'DIED', 'TRANSFER_RESERVED', 'STEPPED_DOWN', 'HANDED_OVER'];
  for (const old of before) {
    const p = after.get(old.id);
    const left = !p || p.seat !== old.seat || p.table !== old.table;
    if (left && events.some(e => e.patient === old.id && e.table === old.table && e.audience.includes(`seat:${old.seat}`) && departures.includes(e.type))) continue;
    const changes = left ? ['No longer on your list'] : [];
    if (!left && p.colour !== old.colour) changes.push(`Triage: ${old.colour.toLowerCase()} → ${p.colour.toLowerCase()}`);
    if (!left && (p.ticks !== old.ticks || p.colour !== old.colour)) changes.push(`Waiting time used: ${old.ticks} of ${clockLength(old.colour)} → ${p.ticks} of ${clockLength(p.colour)}`);
    if (!left && p.route && old.route && p.route.code === old.route.code && p.route.completed !== old.route.completed) changes.push(`Route steps completed: ${old.route.completed} → ${p.route.completed}`);
    if (changes.length) emit(ctx, { type: left ? 'PATIENT_LEFT' : 'PATIENT_CHANGED', table: old.table, seat: old.seat, patient: old.id,
      text: `${old.name}: ${changes.join('; ')}.`, audience: ['faculty', `seat:${old.seat}`] });
  }
}

/** Commit all care before recording the untimed review boundary. */
function commitTurn(ctx: Ctx, start: number): void {
  const before = patientBoundarySnapshot(ctx.s);
  const outcomes = commitDrafts(ctx);
  setPhase(ctx, 'TICK_OPEN');
  tickOpen(ctx);
  setPhase(ctx, 'TICK_CLOSE');
  tickClose(ctx);
  resolveDiagnostics(ctx);
  ctx.s.tables.forEach((t, i) => outcomes[i]!.push(...commitTransfers(ctx, t)));
  leaveAct(ctx);
  for (const t of ctx.s.tables) t.published = { saved: t.dashboard.saved, harmed: t.dashboard.harmed, died: t.dashboard.died };
  setPhase(ctx, 'LOG');
  recordPatientChanges(ctx, before, start);
  recordBoundary(ctx, 'COMMIT', start, outcomes);
}

/** Reconcile outstanding ownership and cancel unplayed work without another tick. */
function settleShift(ctx: Ctx, early: boolean): void {
  const start = ctx.events.length;
  const turn = ctx.s.clock.turn;
  releaseTransfers(ctx);
  resolveDiagnostics(ctx);
  const outcomes = ctx.s.tables.map(t => {
    const committed = t.receipts.some(r => r.turn === turn && r.boundary === 'COMMIT');
    const cancelled = early && !committed ? seatedRoles(t).flatMap(role => (t.drafts[role]?.turn === turn ? t.drafts[role]!.actions : [])
      .map(a => ({ seat: role, actionId: a.id, status: 'CANCELLED' as const, reason: 'Control ended the shift before this action could happen.' }))) : [];
    if (cancelled.length) emit(ctx, { type: 'DRAFTS_CANCELLED', table: t.no, text: 'Control ended the shift; actions still in plans were cancelled.', audience: ['faculty', 'table'] });
    t.drafts = {};
    t.consents = [];
    expireLoans(ctx, t, turn);
    for (const loan of t.board.staffLoans) emit(ctx, { type: 'STAFF_LOAN_CANCELLED', table: t.no, seat: loan.from, text: `The shift ended before the Staff loan to ${loan.to} began.`, audience: ['faculty', `seat:${loan.from}`, `seat:${loan.to}`] });
    t.board.staffLoans = [];
    return cancelled;
  });
  if (outcomes.some(o => o.length)) recordBoundary(ctx, 'COMMIT', start, outcomes);
}

/** Move to the next phase. `from` must match the current turn and phase (stale timers are rejected upstream). */
export function advancePhase(ctx: Ctx): void {
  const c = ctx.s.clock;
  const start = ctx.events.length;
  switch (c.phase) {
    case 'SETUP':
    case 'LOG':
      releaseTurn(ctx, start);
      return;
    case 'ACT':
      commitTurn(ctx, start);
      return;
    case 'HANDOVER':
      setPhase(ctx, 'DEBRIEF');
      enterDebrief(ctx);
      return;
    case 'DEBRIEF':
      setPhase(ctx, 'ENDED');
      return;
    case 'ENDED':
      return;
    default:
      throw new Error(`Cannot advance internal phase ${c.phase}.`);
  }
}
